import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_ORIGIN =
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  "https://romancars.co.uk";

// ------------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------------
function setOrCreateMeta(name, content, useProperty = false) {
  if (!content) return;
  const attr = useProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(url) {
  if (!url) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

function injectJsonLd(id, data) {
  if (!data) return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

// --------------------------------------------------------------------------------
// Legacy content_blocks renderer (kept for backwards compatibility)
// -------------------------------------------------------------------------------
function ContentBlock({ block }) {
  const { type, content } = block;
  const alignClass =
    content.align === "center"
      ? "text-center"
      : content.align === "right"
      ? "text-right"
      : "text-left";

  switch (type) {
    case "heading": {
      const HeadingTag = content.level || "h2";
      const headingClasses = {
        h1: "text-4xl md:text-5xl font-bold mb-6",
        h2: "text-3xl md:text-4xl font-bold mb-5",
        h3: "text-2xl md:text-3xl font-semibold mb-4",
        h4: "text-xl md:text-2xl font-semibold mb-3",
      };
      return (
        <HeadingTag
          className={`${headingClasses[content.level] || headingClasses.h2} ${alignClass}`}
        >
          {content.text}
        </HeadingTag>
      );
    }
    case "paragraph":
      return (
        <p className={`text-gray-600 leading-relaxed mb-4 ${alignClass}`}>
          {content.text}
        </p>
      );
    case "image":
      if (!content.url) return null;
      return (
        <div className={`mb-6 ${alignClass}`}>
          <img
            src={content.url}
            alt={content.alt || ""}
            className={`rounded-lg shadow-md max-w-full h-auto ${
              content.align === "center" ? "mx-auto" : ""
            }`}
          />
        </div>
      );
    case "button": {
      const buttonStyles = {
        primary: "bg-orange-500 hover:bg-orange-600 text-white",
        secondary: "bg-gray-600 hover:bg-gray-700 text-white",
        outline:
          "border-2 border-orange-500 text-orange-500 hover:bg-orange-50",
      };
      return (
        <div className={`mb-6 ${alignClass}`}>
          <a
            href={content.url || "#"}
            className={`inline-block px-6 py-3 rounded-lg font-semibold transition-colors ${
              buttonStyles[content.style] || buttonStyles.primary
            }`}
          >
            {content.text || "Click Here"}
          </a>
        </div>
      );
    }
    case "spacer":
      return <div style={{ height: content.height || 40 }} />;
    case "divider":
      return <hr className="border-gray-200 my-6" />;
    case "hero":
      return (
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-2xl p-8 md:p-12 mb-8">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            {content.title}
          </h1>
          {content.subtitle && (
            <p className="text-lg md:text-xl text-gray-300 mb-6 max-w-2xl">
              {content.subtitle}
            </p>
          )}
          {content.button_text && (
            <a
              href={content.button_url || "/book"}
              className="inline-block px-8 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold transition-colors"
            >
              {content.button_text}
            </a>
          )}
        </div>
      );
    case "intro":
      return (
        <div className="mb-8">
          {content.title && (
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              {content.title}
            </h2>
          )}
          {content.text && (
            <p className="text-gray-600 leading-relaxed text-lg">
              {content.text}
            </p>
          )}
        </div>
      );
    case "features":
      return (
        <div className="mb-8">
          {content.title && (
            <h2 className="text-2xl md:text-3xl font-bold mb-6">
              {content.title}
            </h2>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(content.items || []).map((item, idx) => (
              <div
                key={idx}
                className="bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{btem.description}</p>
              </div>
            ))}
          </div>
        </div>
      );
    case "faq":
      return (
        <div className="mb-8">
          {content.title && (
            <h2 className="text-2xl md:text-3xl font-bold mb-6">
              {content.title}
            </h2>
          )}
          <div className="space-y-4">
            {(content.items || []).map((item, idx) => (
              <div key={idx} className="bg-white border rounded-lg p-5">
                <h3 className="font-semibold text-lg mb-2">{item.question}</h3>
                <p className="text-gray-600">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      );
    case "cta":
      return (
        <div className="bg-orange-500 text-white rounded-2xl p-8 md:p-12 text-center mb-8">
          {content.title && (
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              {content.title}
            </h2>
          )}
          {content.subtitle && (
            <p className="text-orange-100 mb-6 text-lg">{content.subtitle}</p>
          )}
          {content.button_text && (
            <a
              href={content.button_url || "/book"}
              className="inline-block px-8 py-3 bg-white text-orange-500 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              {content.button_text}
            </a>
          )}
        </div>
      );
    case "reviews":
      return (
        <div className="mb-8">
          {content.title && (
            <h2 className="text-2xl md:text-3xl font-bold mb-6">
              {content.title}
            </h2>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            {(content.items || []).map((item, idx) => (
              <div
                key={idx}
                className="bg-white border rounded-xl p-5 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-semibold">{item.name}</span>
                  <span className="text-yellow-500">
                    {"\u2605".repeat(item.rating || 5)}
                  </span>
                </div>
                <p className="text-gray-600 italic">&ldquo;{item.text}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
}

// --------------------------------------------------------------------------------
// EEAT SEO Section renderer (uses HTML directly from CMS / AI)
// -------------------------------------------------------------------------------
function HtmlSection({ title, html }) {
  if (!html || !String(html).trim()) return null;
  return (
    <section className="mb-10">
      {title && (
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900">
          {title}
        </h2>
      )}
      <div
        className="eeat-html-body text-gray-700 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </section>
  );
}

function FaqSection({ faqs }) {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;
  return (
    <section className="mb-10">
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">
        Frequently Asked Questions
      </h2>
      <div className="space-y-3">
        {faqs.map((item, idx) => (
          <details
            key={idx}
            className="bg-white border border-gray-200 rounded-lg p-5 group"
          >
            <summary className="font-semibold text-lg text-gray-900 cursor-pointer list-none flex justify-between items-center">
              <span>{item.question || item.q}</span>
              <span className="text-orange-500 ml-3 group-open:rotate-45 transition-transform text-2xl leading-none">
                +
              </span>
            </summary>
            <div
              className="mt-3 text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: item.answer || item.a || "",
              }}
            />
          </details>
        ))}
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------------
// Main page
// -------------------------------------------------------------------------------
export default function DynamicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [seo, setSeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const [pageRes, seoRes] = await Promise.all([
          axios.get(`${API}/cms/pages/slug/${slug}`),
          axios.get(`${API}/cms/seo/${slug}`).catch(() => ({ data: { seo: null } })),
        ]);
        if (cancelled) return;
        setPage(pageRes.data.page);
        setSeo(seoRes.data.seo);
      } catch (err) {
        if (!cancelled) setError("Page not found");
      }
      if (!cancelled) setLoading(false);
    };
    fetchPage();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // SEO: title, meta, canonical, OG, JSON-LD
  useEffect(() => {
    if (!page) return;

    const metaTitle =
      page.meta_title ||
      seo?.title ||
      page.seo?.title ||
      page.title ||
      "Roman Cars";
    const metaDescription =
      page.meta_description ||
      seo?.meta_description ||
      page.seo?.meta_description ||
      "";
    const metaKeywords =
      page.focus_keyword ||
      seo?.meta_keywords ||
      page.seo?.meta_keywords ||
      "";
    const heroImage = page.hero_image_url || page.og_image || "";
    const canonicalUrl = `${SITE_ORIGIN}/${slug}`;

    document.title = metaTitle;
    setOrCreateMeta("description", metaDescription);
    if (metaKeywords) setOrCreateMeta("keywords", metaKeywords);
    setOrCreateMeta("robots", "index, follow");
    setCanonical(canonicalUrl);

    // Open Graph
    setOrCreateMeta("og:title", metaTitle, true);
    setOrCreateMeta("og:description", metaDescription, true);
    setOrCreateMeta("og:type", "website", true);
    setOrCreateMeta("og:url", canonicalUrl, true);
    if (heroImage) setOrCreateMeta("og:image", heroImage, true);

    // Twitter card
    setOrCreateMeta("twitter:card", "summary_large_image");
    setOrCreateMeta("twitter:title", metaTitle);
    setOrCreateMeta("twitter:description", metaDescription);
    if (heroImage) setOrCreateMeta("twitter:image", heroImage);

    // JSON-LD: TaxiService
    const taxiService = {
      "@context": "https://schema.org",
      "@type": "TaxiService",
      name: metaTitle,
      description: metaDescription,
      provider: {
        "@type": "LocalBusiness",
        name: "Roman Cars",
        url: SITE_ORIGIN,
        telephone: "+44 1753 911 911",
        address: {
          "@type": "PostalAddress",
          addressCountry: "GB",
        },
      },
      areaServed: page.h1 || page.title || metaTitle,
      url: canonicalUrl,
    };
    injectJsonLd("ld-taxiservice", taxiService);

    // JSON-LD: BreadcrumbList
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: SITE_ORIGIN,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: page.h1 || page.title || metaTitle,
          item: canonicalUrl,
        },
      ],
    };
    injectJsonLd("ld-breadcrumb", breadcrumb);

    // JSON-LD: FAQPage
    const faqs = Array.isArray(page.faq) ? page.faq : [];
    if (faqs.length > 0) {
      const faqPage = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.question || item.q || "",
          acceptedAnswer: {
            "@type": "Answer",
            text: (item.answer || item.a || "").replace(/<[^>]+>/g, ""),
          },
        })),
      };
      injectJsonLd("ld-faqpage", faqPage);
    } else {
      removeJsonLd("ld-faqpage");
    }

    return () => {
      document.title = "Roman Cars";
      removeJsonLd("ld-taxiservice");
      removeJsonLd("ld-breadcrumb");
      removeJsonLd("ld-faqpage");
    };
  }, [page, seo, slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-gray-600 mb-6">Page not found</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Detect EEAT-rich payload (new schema)
  const hasEeat = Boolean(
    page.h1 ||
      page.intro_html ||
      page.services_html ||
      page.why_choose_html ||
      page.local_landmarks_html ||
      (Array.isArray(page.faq) && page.faq.length > 0)
  );

  const heroImage = page.hero_image_url || "";
  const heroAlt = page.image_alt || page.h1 || page.title || "Roman Cars";
  const h1 = page.h1 || page.title || "Roman Cars";

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        .eeat-html-body h2 {
          font-size: 1.5rem; font-weight: 700;
          margin-top: 1.75rem; margin-bottom: 0.75rem;
          color: #111827; line-height: 1.3;
        }
        @media (min-width: 768px) { .eeat-html-body h2 { font-size: 1.75rem; } }
        .eeat-html-body h3 {
          font-size: 1.25rem; font-weight: 600;
          margin-top: 1.5rem; margin-bottom: 0.5rem; color: #111827;
        }
        .eeat-html-body p {
          margin-bottom: 1rem; color: #374151; line-height: 1.75;
        }
        .eeat-html-body strong { font-weight: 600; color: #111827; }
        .eeat-html-body ul { list-style: disc; margin-left: 1.5rem; margin-bottom: 1rem; }
        .eeat-html-body ol { list-style: decimal; margin-left: 1.5rem; margin-bottom: 1rem; }
        .eeat-html-body li { margin-bottom: 0.35rem; }
        .eeat-html-body a { color: #ea580c; text-decoration: underline; }
        .eeat-html-body a:hover { color: #c2410c; }
      `}</style>

      {/* Header (shared site header: logo and full menu, same as the homepage) */}
      <Header />

      {/* Breadcrumb */}
      <nav
        aria-label="Breadcrumb"
        className="max-w-4xl mx-auto px-4 pt-6 text-sm text-gray-500"
      >
        <a href="/" className="hover:text-orange-500">
          Home
        </a>
        <span className="mx-2">/</span>
        <span className="text-gray-700">{h1}</span>
      </nav>

      {/* Page Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {hasEeat ? (
          <>
            {/* Hero */}
            <section className="mb-8">
              {heroImage && (
                <img
                  src={heroImage}
                  alt={heroAlt}
                  className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-md mb-6"
                  loading="eager"
                />
              )}
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                {h1}
              </h1>
              {page.meta_description && (
                <p className="text-lg text-gray-600 leading-relaxed">
                  {page.meta_description}
                </p>
              )}
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="/book"
                  className="inline-block px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Book a Taxi Now
                </a>
                <a
                  href="tel:+441753911911"
                  className="inline-block px-6 py-3 border-2 border-orange-500 text-orange-500 hover:bg-orange-50 rounded-lg font-semibold transition-colors"
                >
                  Call: 01753 911 911
                </a>
              </div>
            </section>

            <HtmlSection html={page.intro_html} />
            <HtmlSection title="Our Services" html={page.services_html} />
            <HtmlSection
              title="Why Choose Roman Cars"
              html={page.why_choose_html}
            />
            <HtmlSection
              title="Local Landmarks & Areas We Cover"
              html={page.local_landmarks_html}
            />

            <FaqSection faqs={page.faq} />

            {/* CTA */}
            <section className="bg-orange-500 text-white rounded-2xl p-8 md:p-10 text-center mb-4">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Ready to book your ride?
              </h2>
              <p className="text-orange-100 mb-6 text-lg">
                Licensed PHV drivers. Fixed transparent fares. 24/7 service.
              </p>
              <a
                href="/book"
                className="inline-block px-8 py-3 bg-white text-orange-500 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Book Now
              </a>
            </section>
          </>
        ) : (
          <>
            {page.content_blocks?.map((block, index) => (
              <ContentBlock key={block.id || index} block={block} />
            ))}
            {(!page.content_blocks || page.content_blocks.length === 0) && (
              <div className="text-center py-12">
                <p className="text-gray-500">This page has no content yet.</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer (shared site footer) */}
      <Footer />
    </div>
  );
}
